// The Pick list tool: what Assembly still needs for the runs in progress, and a field to
// pick boxes into it. A scan moves the whole box, says so under the field, then the list
// is fetched again. Between scans the list refetches on a 30 s timer while the tab is
// visible (as the desktop pick page does), since stations raise requests while this sits open.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import SectionChip from '../../components/SectionChip';
import { useWorkOrdersApi, type AssemblyPickList, type PickBox, type PoolLine } from '../../WorkOrders/workOrdersApi';
import BoxScan from '../BoxScan';
import { serverMessage } from '../errors';
import { buzz } from '../shell';
import { Notice } from '../ui';
import { KEY_TONAL } from '../Assembly/WorkOrderStation';

const REFRESH_MS = 30_000;

/** Distinct section codes of a line's boxes, in order; the warehouse name stands in for a box with none. */
export function placesOf(boxes: PickBox[]): { key: string; code: string | null; label: string }[] {
  const seen = new Set<string>();
  const out: { key: string; code: string | null; label: string }[] = [];
  for (const b of boxes) {
    const code = b.section_code || null;
    const label = code || b.warehouse_name || b.location_name || '';
    if (!label || seen.has(label)) continue;
    seen.add(label);
    out.push({ key: label, code, label });
  }
  return out;
}

const Line: React.FC<{ line: PoolLine }> = ({ line }) => {
  const places = placesOf(line.boxes || []);
  return (
    <li className="rounded-xl bg-surface-container px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate text-lg font-semibold text-on-surface">{line.target_name}</span>
        <span className="shrink-0 whitespace-nowrap text-base font-semibold text-primary">
          Move <span className="font-mono tabular-nums">{line.to_move}</span>
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-sm text-on-surface-variant">
        {places.length === 0 ? (
          <span>No box on a shelf holds this.</span>
        ) : (
          places.map((p) => (p.code ? <SectionChip key={p.key} code={p.code} /> : <span key={p.key}>{p.label}</span>))
        )}
        {line.requests?.length > 0 && <span className="text-on-surface">A station is waiting.</span>}
      </div>
    </li>
  );
};

const PickListTool: React.FC = () => {
  const woApi = useWorkOrdersApi();
  const [data, setData] = useState<AssemblyPickList | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [n, setN] = useState(0);
  const busyRef = useRef(false);
  busyRef.current = busy;
  // Only the latest request may land: a timer fetch that began before a pick must not restore the stale list.
  const latest = useRef(0);

  const load = useCallback(async () => {
    const mine = ++latest.current;
    try {
      const next = await woApi.assemblyPickList();
      if (mine !== latest.current) return;
      setData(next);
      setLoadError(null);
    } catch (err) {
      if (mine !== latest.current) return;
      setLoadError(serverMessage(err, 'The pick list did not load.'));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !busyRef.current) load();
    }, REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load]);

  const pick = useCallback(async (code: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await woApi.assemblyPick({ barcode: code });
      const barcode = res?.moved?.[0]?.barcode || code.toUpperCase();
      setDone(`${barcode} is in Assembly.`);
      setN((x) => x + 1);
      await load();
    } catch (err) {
      setDone(null);
      buzz('reject');
      setError(serverMessage(err, 'That did not go through. Scan the box again.'));
    } finally {
      setBusy(false);
    }
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  // The server puts requested lines first; within that, lines a shelf box can fill come before ones none can.
  const lines = (data?.lines || [])
    .filter((l) => l.to_move > 0 || l.requests?.length > 0)
    .map((l, i) => ({ l, i }))
    .sort((a, b) => (b.l.requests?.length > 0 ? 1 : 0) - (a.l.requests?.length > 0 ? 1 : 0)
      || (b.l.boxes.length > 0 ? 1 : 0) - (a.l.boxes.length > 0 ? 1 : 0)
      || a.i - b.i)
    .map(({ l }) => l);

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-3 px-4 pb-6">
      <BoxScan id="scanner-pick-scan" label="Scan a box to pick it" placeholder="Scan or type a box" busy={busy} onScan={pick} />
      <div aria-live="polite">
        {done && <div key={n}><Notice tone="ok">{done}</Notice></div>}
        {error && <div role="alert"><Notice tone="miss">{error}</Notice></div>}
      </div>

      {data === null && !loadError && <p role="status" className="px-1 text-base text-on-surface-variant">Loading the pick list…</p>}
      {loadError && (
        <div role="alert" className="flex flex-col gap-2">
          <Notice tone="miss">{data ? `${loadError} What is shown may be out of date.` : loadError}</Notice>
          <button type="button" onClick={load} className={KEY_TONAL}>Try again</button>
        </div>
      )}
      {data && lines.length === 0 && (
        <p className="rounded-xl bg-surface-container-low px-4 py-3 text-base text-on-surface-variant">
          Assembly has everything the runs need.
        </p>
      )}
      {lines.length > 0 && (
        <ul aria-label="Parts to move" className="flex flex-col gap-2">
          {lines.map((l) => <Line key={l.key} line={l} />)}
        </ul>
      )}
    </div>
  );
};

export default PickListTool;
